import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import config from '../../config';

const API_KEY = config.googleMapsApiKey || process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/**
 * ModalMap
 * Nested Google Map component for displaying selected locations directly inside the CityExplorer Modal.
 */
const ModalMap = ({ locations = [] }) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);

  // Initialize Google Map
  useEffect(() => {
    if (!window.google || !mapContainerRef.current) return;

    const map = new window.google.maps.Map(mapContainerRef.current, {
      center: { lat: 13.3439188, lng: 74.7474504 }, // Udupi default
      zoom: 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    mapInstanceRef.current = map;
    infoWindowRef.current = new window.google.maps.InfoWindow();

    return () => {
      markersRef.current.forEach(m => m.setMap(null));
      markersRef.current = [];
      mapInstanceRef.current = null;
      infoWindowRef.current = null;
    };
  }, []);

  // Update Markers when locations change
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || typeof window.google === 'undefined') return;

    // Clear previous markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    if (locations.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    locations.forEach((loc, idx) => {
      if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') return;
      
      const marker = new window.google.maps.Marker({
        position: { lat: loc.lat, lng: loc.lng },
        map,
        title: loc.label,
        zIndex: 920 + idx,
        icon: {
          path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
          fillColor: '#6366F1', // indigo
          fillOpacity: 0.95,
          strokeColor: '#ffffff',
          strokeWeight: 2,
          scale: 1.35,
          anchor: { x: 12, y: 22 },
        }
      });

      marker.addListener('click', () => {
        if (infoWindowRef.current) {
          infoWindowRef.current.setContent(`
            <div style="padding: 4px; font-family: sans-serif; font-size: 13px;">
              <strong>📍 ${loc.label || 'Selected Location'}</strong>
              ${loc.city ? `<div style="color: #64748b; font-size: 11px; margin-top: 2px;">City: ${loc.city}</div>` : ''}
            </div>
          `);
          infoWindowRef.current.open(map, marker);
        }
      });

      markersRef.current.push(marker);
      bounds.extend({ lat: loc.lat, lng: loc.lng });
    });

    // Fit map bounds
    if (locations.length > 1) {
      try {
        map.fitBounds(bounds);
      } catch (_) {}
    } else if (locations.length === 1) {
      const first = locations[0];
      try {
        map.panTo({ lat: first.lat, lng: first.lng });
        map.setZoom(14);
      } catch (_) {}
    }
  }, [locations]);

  return (
    <div 
      ref={mapContainerRef} 
      style={{ 
        width: '100%', 
        height: '100%', 
        minHeight: '440px', 
        borderRadius: 12, 
        border: '1px solid #cbd5e1',
        boxShadow: '0 4px 16px rgba(15, 23, 42, 0.08)'
      }} 
    />
  );
};

/**
 * CityExplorer
 * Dynamic city/place navigator powered by Google Places API (New) & DB fallback.
 * Allows searching cities, listing sublocalities/bus stops, searching sub-places inside a sublocality,
 * and bulk-marking locations directly on an integrated side-by-side modal map.
 */
const CityExplorer = ({ onMarkLocation, onClearHighlights, onClose, initialSearch = '' }) => {
  const [allCities, setAllCities] = useState([]); // [{city, pincodes: string[] }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [googleSuggestions, setGoogleSuggestions] = useState([]);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  const [selectedCity, setSelectedCity] = useState(null); // string (city name)
  const [selectedCityData, setSelectedCityData] = useState(null); // { city, pincodes, isGoogle?, placeObj? }
  const [activeTab, setActiveTab] = useState('places'); // 'places' | 'pincodes'

  // Sublocalities & bus stops in selected city
  const [cityPlaces, setCityPlaces] = useState([]);
  const [loadingPlaces, setLoadingPlaces] = useState(false);

  // Selected sublocality detail
  const [selectedSubArea, setSelectedSubArea] = useState(null); // place object
  const [subPlaces, setSubPlaces] = useState([]);
  const [loadingSubPlaces, setLoadingSubPlaces] = useState(false);

  const [pincodeMeta, setPincodeMeta] = useState({}); // { pincode: { lat, lng, locality } }
  const [geocodingPins, setGeocodingPins] = useState(new Set());

  // Local map highlights for the embedded modal map
  const [localHighlights, setLocalHighlights] = useState([]);

  // Fetch db cities on mount
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const base = (config && (config.opsApiBase || config.opsApiUrl)) || 'http://localhost:8000/api/v1/ops';
    const url = `${base.replace(/\/$/, '')}/cities?include_pincodes=true&limit=1000`;

    fetch(url, { credentials: 'include' })
      .then(async (r) => {
        const text = await r.text();
        try {
          return JSON.parse(text);
        } catch (e) {
          const snippet = text.slice(0, 120).replace(/\s+/g, ' ');
          throw new Error(`Expected JSON from ${url} but got: ${snippet}`);
        }
      })
      .then((data) => {
        if (cancelled) return;
        if (data && data.success && Array.isArray(data.cities)) {
          const normalized = data.cities.map((c) => ({
            city: c.city,
            pincodes: Array.isArray(c.pincodes) ? c.pincodes.map(String) : [],
          }));
          setAllCities(normalized);
        } else {
          setError(data?.error || 'Failed to load database cities');
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Network error loading cities');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Debounced fetch for Google Places Autocomplete suggestions
  useEffect(() => {
    const q = searchTerm.trim();
    if (!q || q.length < 2) {
      setGoogleSuggestions([]);
      return;
    }

    const delay = setTimeout(async () => {
      setLoadingGoogle(true);
      try {
        const res = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': API_KEY,
          },
          body: JSON.stringify({
            input: q,
            includedRegionCodes: ['in'],
          }),
        });
        const data = await res.json();
        const items = (data.suggestions || [])
          .filter((s) => s.placePrediction)
          .map((s) => ({
            placeId: s.placePrediction.placeId,
            text: s.placePrediction.text?.text || '',
            mainText: s.placePrediction.structuredFormat?.mainText?.text || '',
            secondaryText: s.placePrediction.structuredFormat?.secondaryText?.text || '',
          }));
        setGoogleSuggestions(items);
      } catch (err) {
        console.error('[Autocomplete] error:', err);
      } finally {
        setLoadingGoogle(false);
      }
    }, 400);

    return () => clearTimeout(delay);
  }, [searchTerm]);

  // Filtered db cities list
  const filteredDbCities = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return allCities;
    return allCities.filter((c) =>
      c.city.toLowerCase().includes(q) ||
      c.pincodes.some((p) => p.toLowerCase().includes(q))
    );
  }, [allCities, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredDbCities.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageCities = filteredDbCities.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Geocode city (fallback center)
  const geocodeCity = useCallback((cityName) => {
    return new Promise((resolve) => {
      const g = (typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.Geocoder)
        ? new window.google.maps.Geocoder()
        : null;
      if (!g) {
        resolve(null);
        return;
      }
      g.geocode({ address: `${cityName}, Karnataka, India` }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const loc = results[0].geometry.location;
          resolve({ lat: loc.lat(), lng: loc.lng(), locality: cityName });
        } else {
          resolve(null);
        }
      });
    });
  }, []);

  // Fetch localities, neighborhoods, and bus stops inside selected city/area
  // placeLat/placeLng: optional coords of the place for fallback to fetchSubPlaces
  const fetchCityPlaces = async (cityName, placeLat, placeLng) => {
    setLoadingPlaces(true);
    setCityPlaces([]);
    setSelectedSubArea(null);
    setSubPlaces([]);
    try {
      const cleanCityName = cityName.split(',')[0].trim();
      const headers = {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': API_KEY,
        'X-Goog-FieldMask': 'places.displayName,places.formattedAddress,places.location,places.types,places.id',
      };

      const mapOfPlaces = new Map();

      const runQuery = async (queryText) => {
        try {
          const res = await fetch('https://places.googleapis.com/v1/places:searchText', {
            method: 'POST',
            headers,
            body: JSON.stringify({
              textQuery: queryText,
              pageSize: 40
            })
          }).then(r => r.json()).catch(() => ({}));

          const places = res.places || [];
          places.forEach((p) => {
            if (!p.id) return;
            const name = p.displayName?.text || '';
            const types = p.types || [];
            const lat = p.location?.latitude;
            const lng = p.location?.longitude;
            if (lat == null || lng == null) return;

            // Skip exact matches with the city itself
            if (name.toLowerCase() === cleanCityName.toLowerCase() || name.toLowerCase() === cityName.toLowerCase()) return;

            // Apply strict filtering to only keep main area names (sublocalities/neighborhoods)
            const isArea = types.some(t => t.includes('sublocality') || t === 'locality' || t === 'neighborhood' || t === 'political');
            const isExcluded = types.some(t => [
              'transit_station', 'bus_stop', 'transit_depot', 'park', 
              'store', 'lodging', 'restaurant', 'food', 'establishment',
              'point_of_interest', 'shopping_mall'
            ].includes(t));

            if (isArea && !isExcluded) {
              mapOfPlaces.set(p.id, {
                id: p.id,
                name,
                address: p.formattedAddress || '',
                lat,
                lng,
                types,
              });
            }
          });
        } catch (e) {
          console.error(`Error fetching query: ${queryText}`, e);
        }
      };

      // Query 1: neighborhoods and sublocalities (gives clean localities for Mangaluru and Udupi)
      await runQuery(`neighborhoods and sublocalities in ${cleanCityName}, Karnataka`);

      // Fallback: If less than 3 areas are found, fetch neighborhoods and areas to cover smaller cities like Bantwal
      if (mapOfPlaces.size < 3) {
        await runQuery(`neighborhoods and areas in ${cleanCityName}, Karnataka`);
      }

      const items = Array.from(mapOfPlaces.values()).sort((a, b) => a.name.localeCompare(b.name));
      setCityPlaces(items);

      // If still no sub-areas found (e.g. Malpe is a small village with no sub-localities),
      // fall back to showing places/landmarks directly inside this location
      if (items.length === 0 && placeLat != null && placeLng != null) {
        setLoadingPlaces(false);
        fetchSubPlaces(cleanCityName, cleanCityName, placeLat, placeLng);
        return;
      }
    } catch (err) {
      console.error('Error fetching city places:', err);
    } finally {
      setLoadingPlaces(false);
    }
  };

  // Fetch stops, places and landmarks inside selected sublocality (e.g. Malpe)
  const fetchSubPlaces = async (areaName, parentCityName, areaLat, areaLng) => {
    setLoadingSubPlaces(true);
    setSubPlaces([]);
    try {
      const headers = {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': API_KEY,
        'X-Goog-FieldMask': 'places.displayName,places.formattedAddress,places.location,places.types,places.id',
      };

      // Use locationBias (circle) to guide Google toward this area.
      // Note: searchText only supports circle with locationBias, not locationRestriction.
      // The client-side Haversine filter below strictly enforces the 2km boundary.
      const locationBias = (areaLat != null && areaLng != null) ? {
        circle: {
          center: { latitude: areaLat, longitude: areaLng },
          radius: 2000.0  // 2 km bias radius
        }
      } : undefined;

      const buildBody = (query) => {
        const body = { textQuery: query, pageSize: 15 };
        if (locationBias) body.locationBias = locationBias;
        return JSON.stringify(body);
      };

      const [resSub, resStops] = await Promise.all([
        fetch('https://places.googleapis.com/v1/places:searchText', {
          method: 'POST',
          headers,
          body: buildBody(`places, landmarks, and streets in ${areaName}, ${parentCityName}, Karnataka, India`),
        }).then(r => r.json()).catch(() => ({})),
        fetch('https://places.googleapis.com/v1/places:searchText', {
          method: 'POST',
          headers,
          body: buildBody(`bus stops and transit in ${areaName}, ${parentCityName}, Karnataka, India`),
        }).then(r => r.json()).catch(() => ({}))
      ]);

      const places = [...(resSub.places || []), ...(resStops.places || [])];
      const mapOfPlaces = new Map();

      // Calculate max allowed distance from area center (2km)
      const MAX_DIST_KM = 2.0;
      const toRad = (deg) => (deg * Math.PI) / 180;
      const haversine = (lat1, lng1, lat2, lng2) => {
        const R = 6371;
        const dLat = toRad(lat2 - lat1);
        const dLng = toRad(lng2 - lng1);
        const a = Math.sin(dLat / 2) ** 2 +
          Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      };

      places.forEach((p) => {
        if (!p.id) return;
        const name = p.displayName?.text || '';
        const lat = p.location?.latitude;
        const lng = p.location?.longitude;
        if (lat == null || lng == null) return;

        // Skip duplicates of the sublocality itself or city name
        if (name.toLowerCase() === areaName.toLowerCase() || name.toLowerCase() === parentCityName.toLowerCase()) return;

        // Distance filter: reject anything more than 2km from area center
        if (areaLat != null && areaLng != null) {
          const dist = haversine(areaLat, areaLng, lat, lng);
          if (dist > MAX_DIST_KM) return;
        }

        mapOfPlaces.set(p.id, {
          id: p.id,
          name,
          address: p.formattedAddress || '',
          lat,
          lng,
          types: p.types || [],
        });
      });

      const items = Array.from(mapOfPlaces.values()).sort((a, b) => a.name.localeCompare(b.name));
      setSubPlaces(items);

      // Automatically plot all sub-places on the local & dashboard map!
      if (items.length > 0) {
        const markers = items.map(item => ({
          lat: item.lat,
          lng: item.lng,
          label: `${item.name} (${areaName})`,
          city: parentCityName
        }));
        setLocalHighlights(markers);
        onMarkLocation && onMarkLocation(markers);
      }
    } catch (err) {
      console.error('Error fetching sub places:', err);
    } finally {
      setLoadingSubPlaces(false);
    }
  };

  // Select database city

  const handleSelectDbCity = async (cityName) => {
    setSelectedCity(cityName);
    setSelectedSubArea(null);
    setSubPlaces([]);
    const dbCity = allCities.find((c) => c.city === cityName);
    setSelectedCityData(dbCity);
    setActiveTab('places');

    // Pan map to city center
    const center = await geocodeCity(cityName);
    if (center) {
      const highlight = {
        lat: center.lat,
        lng: center.lng,
        label: cityName,
        city: cityName,
      };
      setLocalHighlights([highlight]);
      onMarkLocation && onMarkLocation(highlight);
    }
    // Fetch places inside
    fetchCityPlaces(cityName);
  };

  // Select google place search result
  const handleSelectGooglePlace = async (suggestion) => {
    setSelectedSubArea(null);
    setSubPlaces([]);
    setLoadingPlaces(true);
    try {
      const res = await fetch(`https://places.googleapis.com/v1/places/${suggestion.placeId}`, {
        headers: {
          'X-Goog-Api-Key': API_KEY,
          'X-Goog-FieldMask': 'displayName,formattedAddress,location',
        },
      });
      const place = await res.json();
      const address = place.formattedAddress || place.displayName?.text || suggestion.text;
      const lat = place.location?.latitude;
      const lng = place.location?.longitude;
      const name = place.displayName?.text || suggestion.mainText || address;

      if (lat != null && lng != null) {
        setSelectedCity(name);
        setSelectedCityData({
          city: name,
          pincodes: [],
          isGoogle: true,
          placeObj: { name, lat, lng, address }
        });
        setActiveTab('places');

        const highlight = {
          lat,
          lng,
          label: name,
          city: name
        };
        setLocalHighlights([highlight]);
        onMarkLocation && onMarkLocation(highlight);

        fetchCityPlaces(name, lat, lng);
      }
    } catch (err) {
      console.error('Error getting google place details:', err);
    } finally {
      setLoadingPlaces(false);
    }
  };

  // Select sub-locality area from the list (e.g. Malpe)
  const handleSelectSubArea = (area) => {
    setSelectedSubArea(area);
    const highlight = {
      lat: area.lat,
      lng: area.lng,
      label: area.name,
      city: selectedCity,
    };
    setLocalHighlights([highlight]);
    onMarkLocation && onMarkLocation(highlight);
    fetchSubPlaces(area.name, selectedCity, area.lat, area.lng);
  };

  // Click a specific sub-place (e.g. Malpe Beach)
  const handleMarkSubPlace = (item) => {
    const highlight = {
      lat: item.lat,
      lng: item.lng,
      label: item.name,
      city: selectedCity,
    };
    setLocalHighlights([highlight]);
    onMarkLocation && onMarkLocation(highlight);
  };

  // Database Geocode helper for pincode lists
  const geocodePincode = useCallback((pincode, cityName) => {
    return new Promise((resolve) => {
      const g = (typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.Geocoder)
        ? new window.google.maps.Geocoder()
        : null;

      if (!g) {
        resolve({ lat: null, lng: null, locality: `${cityName} area` });
        return;
      }

      const address = `${pincode}, ${cityName}, Karnataka, India`;
      g.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const place = results[0];
          const loc = place.geometry.location;
          const comps = place.address_components || [];
          const localityComp = comps.find((c) =>
            c.types.includes('sublocality_level_1') ||
            c.types.includes('sublocality') ||
            c.types.includes('locality')
          );
          const locality = localityComp
            ? localityComp.long_name
            : (place.formatted_address || '').split(',')[0] || 'Area';
          resolve({
            lat: loc.lat(),
            lng: loc.lng(),
            locality,
          });
        } else {
          resolve({ lat: null, lng: null, locality: `${cityName} area` });
        }
      });
    });
  }, []);

  // Geocode DB pincodes on activation
  useEffect(() => {
    if (!selectedCityData || selectedCityData.isGoogle) return;
    const pins = selectedCityData.pincodes || [];
    pins.forEach((p, idx) => {
      if (pincodeMeta[p]) return;
      if (geocodingPins.has(p)) return;
      setGeocodingPins((prev) => new Set(prev).add(p));
      setTimeout(() => {
        geocodePincode(p, selectedCityData.city).then((meta) => {
          setPincodeMeta((prev) => ({ ...prev, [p]: meta }));
          setGeocodingPins((prev) => {
            const next = new Set(prev);
            next.delete(p);
            return next;
          });
        });
      }, Math.min(idx * 90, 800));
    });
  }, [selectedCityData, geocodePincode, pincodeMeta, geocodingPins]);

  const handleMarkPincode = async (pincode, city) => {
    let meta = pincodeMeta[pincode];
    if (!meta || meta.lat == null) {
      meta = await geocodePincode(pincode, city);
      if (meta) {
        setPincodeMeta((prev) => ({ ...prev, [pincode]: meta }));
      }
    }
    if (meta && meta.lat != null) {
      const highlight = {
        lat: meta.lat,
        lng: meta.lng,
        label: `${pincode} • ${meta.locality || city}`,
        pincode,
        city,
      };
      setLocalHighlights([highlight]);
      onMarkLocation && onMarkLocation(highlight);
    }
  };

  const clearAll = () => {
    onClearHighlights && onClearHighlights();
    setLocalHighlights([]);
    setSelectedCity(null);
    setSelectedCityData(null);
    setSelectedSubArea(null);
    setSubPlaces([]);
  };

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', fontSize: 15, color: '#64748b' }}>
        <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite', marginRight: 8 }}>⏳</span>
        Loading City Explorer database...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 20, background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: 8, color: '#b91c1c' }}>
        Could not load city data: {error}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 14 }}>
      
      {/* Search & Header control */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260, position: 'relative' }}>
          <input
            type="text"
            placeholder="Type city or area (e.g. Udupi, Malpe, BC Road...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              fontSize: 14,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              outline: 'none',
            }}
          />
          {loadingGoogle && (
            <span style={{ position: 'absolute', right: 12, top: 12, fontSize: 13, color: '#94a3b8' }}>⚡</span>
          )}
        </div>

        <button
          onClick={clearAll}
          style={{
            padding: '8px 14px',
            borderRadius: 8,
            border: '1px solid #cbd5e1',
            background: '#fff',
            fontSize: 13,
            fontWeight: 500,
            color: '#334155',
            cursor: 'pointer',
          }}
        >
          Clear Marks
        </button>

        {onClose && (
          <button
            onClick={onClose}
            style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              background: '#f1f5f9',
              fontSize: 13,
              fontWeight: 500,
              color: '#334155',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        )}
      </div>

      {/* Split screen layout: Left Explorer (55%), Right Map (45%) */}
      <div style={{ display: 'flex', gap: 16, flex: 1, height: 'calc(100% - 50px)', overflow: 'hidden' }}>
        
        {/* Left Panel: Search results + details (55% width) */}
        <div style={{
          width: '55%',
          display: 'flex',
          gap: 16,
          height: '100%',
          overflow: 'hidden'
        }}>
          
          {/* Left Column: Search matching list (38% width) */}
          <div style={{
            width: '38%',
            borderRight: '1px solid #e2e8f0',
            paddingRight: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            overflowY: 'auto'
          }}>
            {/* Google Autocomplete matches */}
            {googleSuggestions.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#6366f1', letterSpacing: 0.5 }}>
                  Google Places Matches
                </div>
                {googleSuggestions.map((s) => (
                  <div
                    key={s.placeId}
                    onClick={() => handleSelectGooglePlace(s)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 8,
                      background: '#f5f3ff',
                      border: '1px solid #ddd6fe',
                      cursor: 'pointer',
                      fontSize: 12,
                      transition: 'all 100ms ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#ede9fe'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#f5f3ff'}
                  >
                    <span style={{ fontWeight: 600, color: '#4c1d95' }}>📍 {s.mainText}</span>
                    <div style={{ fontSize: 10, color: '#7c3aed', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.secondaryText || 'Area'}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* DB fallback matches */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#64748b', letterSpacing: 0.5 }}>
                Serviceable Cities ({filteredDbCities.length})
              </div>
              {pageCities.length === 0 && googleSuggestions.length === 0 && (
                <div style={{ padding: 12, color: '#94a3b8', fontSize: 12, textAlign: 'center' }}>No matching cities.</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {pageCities.map((c) => {
                  const isActive = c.city === selectedCity;
                  return (
                    <div
                      key={c.city}
                      onClick={() => handleSelectDbCity(c.city)}
                      style={{
                        padding: '8px 10px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontSize: 12,
                        background: isActive ? '#eef2ff' : '#fafafa',
                        border: isActive ? '1px solid #818cf8' : '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 120ms ease'
                      }}
                      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#f1f5f9'; }}
                      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = '#fafafa'; }}
                    >
                      <span style={{ fontWeight: isActive ? 700 : 500, color: isActive ? '#3730a3' : '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.city}>
                        🏢 {c.city}
                      </span>
                      <span style={{
                        fontSize: 9,
                        padding: '1px 5px',
                        borderRadius: 999,
                        background: isActive ? '#6366f1' : '#e2e8f0',
                        color: isActive ? '#fff' : '#475569',
                        fontWeight: 600,
                      }}>
                        {c.pincodes.length}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* DB pagination */}
              {filteredDbCities.length > pageSize && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 8, fontSize: 10, color: '#64748b' }}>
                  <button
                    disabled={safePage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    style={{ padding: '1px 6px', borderRadius: 4, border: '1px solid #cbd5e1', background: '#fff', cursor: safePage === 1 ? 'not-allowed' : 'pointer' }}
                  >
                    Prev
                  </button>
                  <span>{safePage} / {totalPages}</span>
                  <button
                    disabled={safePage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    style={{ padding: '1px 6px', borderRadius: 4, border: '1px solid #cbd5e1', background: '#fff', cursor: safePage === totalPages ? 'not-allowed' : 'pointer' }}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>

          </div>

          {/* Right Column: Selected City detail lists (62% width) */}
          <div style={{
            width: '62%',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            paddingLeft: 4
          }}>
            {selectedCityData ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                
                {/* Header title */}
                <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                    {selectedCityData.city}
                  </h3>
                  <p style={{ fontSize: 11, color: '#64748b', margin: '2px 0 0 0' }}>
                    {selectedCityData.isGoogle ? 'Google Search Match' : 'Serviceable Logistics Area'}
                  </p>
                </div>

                {/* Tab layout */}
                {!selectedCityData.isGoogle && (
                  <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid #e2e8f0', paddingBottom: 1 }}>
                    <button
                      onClick={() => setActiveTab('places')}
                      style={{
                        padding: '4px 10px',
                        fontSize: 12,
                        fontWeight: 600,
                        border: 'none',
                        background: 'none',
                        color: activeTab === 'places' ? '#6366f1' : '#64748b',
                        borderBottom: activeTab === 'places' ? '2px solid #6366f1' : '2px solid transparent',
                        cursor: 'pointer',
                      }}
                    >
                      Areas &amp; Stops
                    </button>
                    <button
                      onClick={() => setActiveTab('pincodes')}
                      style={{
                        padding: '4px 10px',
                        fontSize: 12,
                        fontWeight: 600,
                        border: 'none',
                        background: 'none',
                        color: activeTab === 'pincodes' ? '#6366f1' : '#64748b',
                        borderBottom: activeTab === 'pincodes' ? '2px solid #6366f1' : '2px solid transparent',
                        cursor: 'pointer',
                      }}
                    >
                      Pincodes
                    </button>
                  </div>
                )}

                {/* Tab Content: Places */}
                {activeTab === 'places' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
                        Localities &amp; Transit Hubs:
                      </div>
                      {cityPlaces.length > 0 && (
                        <button
                          onClick={() => {
                            const markers = cityPlaces.map(p => ({ lat: p.lat, lng: p.lng, label: p.name, city: selectedCity }));
                            setLocalHighlights(markers);
                            onMarkLocation && onMarkLocation(markers);
                          }}
                          style={{ fontSize: 10, padding: '2px 6px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                        >
                          Mark All
                        </button>
                      )}
                    </div>

                    {loadingPlaces || (cityPlaces.length === 0 && loadingSubPlaces) ? (
                      <div style={{ fontSize: 12, color: '#64748b', padding: '6px 0' }}>🔍 Loading locations from Google Maps...</div>
                    ) : cityPlaces.length === 0 && subPlaces.length > 0 ? (
                      /* Fallback mode: small locality (e.g. Malpe) — show its places/stops directly */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ fontSize: 11, color: '#7e22ce', fontStyle: 'italic', marginBottom: 4 }}>
                          📍 Places in {selectedCity} ({subPlaces.length} found)
                        </div>
                        {subPlaces.map((item) => (
                          <div
                            key={item.id}
                            onClick={() => handleMarkSubPlace(item)}
                            style={{
                              padding: '8px 12px',
                              borderRadius: 8,
                              background: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              transition: 'all 120ms ease'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 13 }}>📍</span>
                              <span style={{ fontWeight: 600, fontSize: 12, color: '#334155' }}>{item.name}</span>
                            </div>
                            <span style={{ fontSize: 9, color: '#64748b', background: '#e2e8f0', padding: '2px 6px', borderRadius: 4 }}>
                              {item.types?.includes('transit_station') || item.types?.includes('bus_stop') ? '🚌 Stop' : '📍 Place'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : cityPlaces.length === 0 ? (
                      <div style={{ fontSize: 12, color: '#94a3b8', padding: '6px 0' }}>No localities or stops returned.</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {cityPlaces.map((p) => {
                          const isSubActive = selectedSubArea?.id === p.id;
                          return (
                            <div key={p.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <div
                                onClick={() => {
                                  if (isSubActive) {
                                    setSelectedSubArea(null);
                                    setSubPlaces([]);
                                  } else {
                                    handleSelectSubArea(p);
                                  }
                                }}
                                style={{
                                  padding: '10px 12px',
                                  borderRadius: 8,
                                  background: isSubActive ? '#eef2ff' : '#f8fafc',
                                  border: isSubActive ? '1px solid #6366f1' : '1px solid #e2e8f0',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  transition: 'all 120ms ease'
                                }}
                                onMouseEnter={(e) => { if (!isSubActive) e.currentTarget.style.background = '#f1f5f9'; }}
                                onMouseLeave={(e) => { if (!isSubActive) e.currentTarget.style.background = '#f8fafc'; }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ fontSize: 14 }}>📍</span>
                                  <span style={{ fontWeight: 600, fontSize: 12, color: isSubActive ? '#3730a3' : '#334155' }}>
                                    {p.name}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ fontSize: 9, color: '#64748b', textTransform: 'capitalize', background: '#e2e8f0', padding: '2px 6px', borderRadius: 4 }}>
                                    {p.types.includes('transit_station') || p.types.includes('bus_stop') ? '🚌 Stop' : '📍 Area'}
                                  </span>
                                  <span style={{ fontSize: 10, color: '#64748b' }}>
                                    {isSubActive ? '▼' : '▶'}
                                  </span>
                                </div>
                              </div>

                              {/* Sub-places nested inline scroll-down dropdown */}
                              {isSubActive && (
                                <div style={{
                                  marginLeft: 12,
                                  padding: '10px 12px',
                                  background: '#faf5ff',
                                  borderRadius: 8,
                                  border: '1px solid #f3e8ff',
                                  borderLeft: '3px solid #818cf8',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 6
                                }}>
                                  {loadingSubPlaces ? (
                                    <div style={{ fontSize: 11, color: '#7c3aed', padding: '4px 0' }}>⚡ Fetching places in {p.name}...</div>
                                  ) : subPlaces.length === 0 ? (
                                    <div style={{ fontSize: 11, color: '#94a3b8', padding: '4px 0' }}>No sub-places found.</div>
                                  ) : (
                                    <>
                                      <div style={{ fontSize: 10, color: '#7e22ce', fontStyle: 'italic', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>* Plotted {subPlaces.length} places on maps (Indigo Pins)</span>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedSubArea(null);
                                            setSubPlaces([]);
                                          }}
                                          style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, border: '1px solid #d8b4fe', background: '#fff', cursor: 'pointer', color: '#7e22ce' }}
                                        >
                                          Hide
                                        </button>
                                      </div>
                                      <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 4,
                                        maxHeight: '220px',
                                        overflowY: 'auto',
                                        paddingRight: 4
                                      }}>
                                        {subPlaces.map((item) => (
                                          <div
                                            key={item.id}
                                            onClick={() => handleMarkSubPlace(item)}
                                            style={{
                                              padding: '6px 10px',
                                              borderRadius: 6,
                                              background: '#fff',
                                              border: '1px solid #f3e8ff',
                                              fontSize: 11,
                                              cursor: 'pointer',
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              alignItems: 'center',
                                              gap: 4,
                                              boxShadow: '0 1px 1px rgba(0,0,0,0.02)'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#faf5ff'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                                          >
                                            <span style={{ fontWeight: 500, color: '#581c87', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }} title={item.name}>
                                              {item.name}
                                            </span>
                                            <span style={{ fontSize: 9, color: '#7e22ce', border: '1px solid #e9d5ff', padding: '0px 4px', borderRadius: 4, background: '#faf5ff' }}>
                                              Pan
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                  </div>
                )}

                {/* Tab Content: Pincodes */}
                {activeTab === 'pincodes' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
                      Registered Database Pincodes:
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 6 }}>
                      {selectedCityData.pincodes.map((p) => {
                        const meta = pincodeMeta[p];
                        const locality = meta ? (meta.locality || 'Area') : (geocodingPins.has(p) ? 'Resolving area…' : '—');
                        return (
                          <div
                            key={p}
                            onClick={() => handleMarkPincode(p, selectedCityData.city)}
                            style={{
                              border: '1px solid #e2e8f0',
                              borderRadius: 6,
                              padding: '6px 8px',
                              background: '#fafafa',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 4,
                            }}
                            title="Click to mark this pincode on the map"
                          >
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: 11 }}>{p}</div>
                              <div style={{ fontSize: 10, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {locality}
                              </div>
                            </div>
                            <span style={{ fontSize: 9, color: '#6366f1', fontWeight: 600 }}>Mark</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>
            ) : (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#94a3b8',
                textAlign: 'center',
                padding: '80px 20px',
                border: '2px dashed #e2e8f0',
                borderRadius: 12,
              }}>
                <span style={{ fontSize: 32, marginBottom: 8 }}>🗺️</span>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#64748b' }}>No City Selected</div>
                <div style={{ fontSize: 11, marginTop: 4 }}>Select a city from the list or search using Google Places to begin.</div>
              </div>
            )}
          </div>

        </div>

        {/* Right Panel: Integrated side-by-side Map (45% width) */}
        <div style={{ width: '45%', height: '100%', display: 'flex', flexDirection: 'column' }}>
          <ModalMap locations={localHighlights} />
        </div>

      </div>

    </div>
  );
};

export default CityExplorer;
